import React from "react";
import { observer } from "mobx-react";
import {
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import AppTablePagenation from "../../../../../../views/components/common/pages/AppTablePagenation";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { trackDailyTasksAction } from "../../../../../../analytics/dailyTasksTracking";
import { DailyTasksFilters } from "../../../../../../models/Dashboard/DailyTasks/DailyTasksFilters";
import { SortType } from "../../../../../../models/SortType";
import {
  DailyTasksListRow,
  DailyTasksSortBy,
  DailyTasksStage,
  FILE_WORK_STATE_LABELS,
  DailyTasksWorkState,
  MOCKUP_WORK_STATE_LABELS,
  MockupWorkState,
  PO_WORK_STATE_LABELS,
  PR_WORK_STATE_LABELS,
  pendingApprovalLabels,
  PRINT_WORK_STATE_LABELS,
  PRINT_IN_WORK_STATE_LABELS,
  PROJECT_WORK_STATE_LABELS,
  RAW_WORK_STATE_LABELS,
  VARIANTS_COVERAGE_LABELS,
  variantsCoverage,
  PoWorkState,
  PrWorkState,
  PrintWorkState,
  PrintInWorkState,
  ProjectWorkState,
  RawWorkState,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import { formatDateWithoutTime } from "../../../../../../views/components/common/functions/formatDate";
import { DailyTasksChipTone, dailyTasksChipSx } from "./dailyTasksChips";
import { useDailyTasksFieldPermissions } from "./useDailyTasksFieldPermissions";
import { dailyTasksStageUi } from "./DailyTasksStageUi";

interface DailyTasksTableProps {
  rows: DailyTasksListRow[];
  stage: DailyTasksStage;
  page_no: number;
  limit: number;
  total_count: number;
  sortBy: DailyTasksSortBy;
  sortType: SortType;
  filters?: DailyTasksFilters | null;
  onSortChange: (column: DailyTasksSortBy, sortType: SortType) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onRowClick: (row: DailyTasksListRow) => void;
}

const CELL_WEIGHT = 500;
const CELL_SIZE = "0.8rem";

const workStateLabel = (
  state: DailyTasksWorkState | null | undefined,
): string => {
  if (!state) return "-";
  if (state in MOCKUP_WORK_STATE_LABELS) {
    return MOCKUP_WORK_STATE_LABELS[state as MockupWorkState];
  }
  if (state in FILE_WORK_STATE_LABELS) {
    return FILE_WORK_STATE_LABELS[state as keyof typeof FILE_WORK_STATE_LABELS];
  }
  if (state in PR_WORK_STATE_LABELS) {
    return PR_WORK_STATE_LABELS[state as PrWorkState];
  }
  if (state in PO_WORK_STATE_LABELS) {
    return PO_WORK_STATE_LABELS[state as PoWorkState];
  }
  if (state in RAW_WORK_STATE_LABELS) {
    return RAW_WORK_STATE_LABELS[state as RawWorkState];
  }
  if (state in PRINT_WORK_STATE_LABELS) {
    return PRINT_WORK_STATE_LABELS[state as PrintWorkState];
  }
  if (state in PRINT_IN_WORK_STATE_LABELS) {
    return PRINT_IN_WORK_STATE_LABELS[state as PrintInWorkState];
  }
  if (state in PROJECT_WORK_STATE_LABELS) {
    return PROJECT_WORK_STATE_LABELS[state as ProjectWorkState];
  }
  return state;
};

const formatHours = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(Number(hours))) return "-";
  const value = Math.round(Number(hours));
  if (value < 24) return `${value}h`;
  const days = Math.floor(value / 24);
  const rem = value % 24;
  return rem ? `${days}d ${rem}h` : `${days}d`;
};

const slaChipTone = (
  status: DailyTasksListRow["sla_status"],
): DailyTasksChipTone => {
  if (status === "bad") return "bad";
  if (status === "warn") return "warn";
  if (status === "ok") return "ok";
  return "neutral";
};

const workStateChipTone = (
  state: DailyTasksWorkState | null,
): DailyTasksChipTone => {
  if (
    state === "needs_upload" ||
    state === "needs_file" ||
    state === "needs_pr" ||
    state === "needs_po" ||
    state === "needs_inward" ||
    state === "needs_outward" ||
    state === "needs_print_in" ||
    state === "needs_request"
  ) {
    return "bad";
  }
  if (
    state === "changes_requested" ||
    state === "needs_send" ||
    state === "email_to_vendor" ||
    state === "needs_qc" ||
    state === "qc_in_progress" ||
    state === "needs_print_qc" ||
    state === "print_qc_in_progress" ||
    state === "awaiting_finance"
  ) {
    return "warn";
  }
  if (state === "qc_on_hold" || state === "print_qc_on_hold") {
    return "bad";
  }
  if (
    state === "awaiting_approval" ||
    state === "partial_pr" ||
    state === "partial_po" ||
    state === "partial_inward" ||
    state === "partial_outward" ||
    state === "partial_print_in" ||
    state === "variants_incomplete"
  ) {
    return "info";
  }
  if (state === "pending_approval") {
    return "warn";
  }
  return "neutral";
};
const formatSlaChip = (
  hours: number | null | undefined,
  target: number | null | undefined,
): string => {
  if (hours == null || !Number.isFinite(Number(hours))) return "-";
  const elapsed = formatHours(hours);
  if (target == null || !Number.isFinite(Number(target))) return elapsed;
  return `${elapsed} / ${formatHours(target)}`;
};

const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  return formatDateWithoutTime(value) || "-";
};

const formatDateList = (
  values: Array<string | Date> | string | null | undefined,
): string => {
  if (values == null) return "-";
  const list = Array.isArray(values)
    ? values
    : String(values)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
  if (list.length === 0) return "-";
  return list.map((item) => formatDate(item)).join(", ");
};

const previewForStage = (
  row: DailyTasksListRow,
  stage: DailyTasksStage,
): string => {
  if (stage === "mockup_upload" || stage === "mockup_approval") {
    const who = row.last_uploaded_by ? ` · ${row.last_uploaded_by}` : "";
    return `${row.mockup_count} mockup${row.mockup_count === 1 ? "" : "s"}${who}`;
  }
  if (stage === "file_upload" || stage === "cdr_to_printer") {
    return `${row.file_count} file${row.file_count === 1 ? "" : "s"}`;
  }
  if (stage === "projects") {
    const finance = row.project_finance_status ?? "none";
    const variants = `${row.variants_qty ?? 0}/${row.ordered_quantity}`;
    const invoice = row.customer_invoice_present ? "invoiced" : "no invoice";
    return `${finance} · ${variants} · ${invoice}`;
  }
  if (stage === "pr_creation") {
    const qty = row.pr_qty ?? 0;
    const ordered = row.ordered_quantity ?? 0;
    return `${qty}/${ordered}`;
  }
  if (stage === "po_creation") {
    const issued = row.issued_po_count ?? 0;
    const approved = row.approved_pr_count ?? 0;
    const sent = row.sent_po_count ?? 0;
    return `${issued}/${approved} PO · ${sent}/${issued} emailed`;
  }
  if (stage === "raw_inward" || stage === "raw_inward_qc") {
    const received = row.received_quantity ?? 0;
    const ordered = row.ordered_po_quantity ?? row.ordered_quantity ?? 0;
    const rejected = row.rejected_quantity ?? 0;
    const state = workStateLabel(row.mockup_work_state);
    return `${received}/${ordered}${rejected > 0 ? ` · ${rejected} rejected` : ""} · ${state}`;
  }
  if (stage === "print_inward" || stage === "print_inward_qc") {
    const received = row.received_quantity ?? 0;
    const ordered = row.ordered_po_quantity ?? row.ordered_quantity ?? 0;
    const rejected = row.rejected_quantity ?? 0;
    const state = workStateLabel(row.mockup_work_state);
    return `${received}/${ordered}${rejected > 0 ? ` · ${rejected} rejected` : ""} · ${state}`;
  }
  if (stage === "print_outward") {
    const sent = row.sent_quantity ?? 0;
    const ordered = row.ordered_quantity ?? 0;
    const state = workStateLabel(row.mockup_work_state);
    return `${sent}/${ordered} · ${state}`;
  }
  const received = row.received_quantity ?? 0;
  const ordered = row.ordered_po_quantity ?? row.ordered_quantity ?? 0;
  return `${row.po_count} PO${row.po_count === 1 ? "" : "s"} · ${received}/${ordered}`;
};

/** Table status: Partial and/or Sourcing/Finance pending can both show. */
const prTableStatusChips = (
  row: DailyTasksListRow,
): Array<{ label: string; tone: DailyTasksChipTone }> => {
  const createdQty = Number(row.created_pr_qty ?? 0);
  if (createdQty <= 0) {
    return [{ label: PR_WORK_STATE_LABELS.needs_pr, tone: "bad" }];
  }
  const chips: Array<{ label: string; tone: DailyTasksChipTone }> = [
    { label: PR_WORK_STATE_LABELS.partial_pr, tone: "info" },
  ];
  for (const name of pendingApprovalLabels(row)) {
    chips.push({ label: `${name} pending`, tone: "warn" });
  }
  return chips;
};

/** Raw status + reject chip so partial QC is visible without a fifth card. */
const rawTableStatusChips = (
  row: DailyTasksListRow,
): Array<{ label: string; tone: DailyTasksChipTone }> => {
  const state = row.mockup_work_state;
  const chips: Array<{ label: string; tone: DailyTasksChipTone }> = [];
  if (state) {
    chips.push({
      label: workStateLabel(state),
      tone: workStateChipTone(state),
    });
  }
  const rejected = Number(row.rejected_quantity ?? 0);
  if (rejected > 0) {
    chips.push({
      label: `${rejected} rejected`,
      tone: "bad",
    });
  }
  return chips.length > 0 ? chips : [{ label: "-", tone: "neutral" }];
};

const projectFinanceChip = (
  status: DailyTasksListRow["project_finance_status"],
): { label: string; tone: DailyTasksChipTone } | null => {
  if (status === "approved") return { label: "Approved", tone: "ok" };
  if (status === "rejected") return { label: "Rejected", tone: "bad" };
  if (status === "pending") return { label: "Pending", tone: "warn" };
  return null;
};

const PrQtyCell: React.FC<{ row: DailyTasksListRow }> = ({ row }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const qty = row.pr_qty ?? 0;
  const ordered = row.ordered_quantity ?? 0;
  const vendor = (row.pr_vendor_name || "").trim();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.25 }}>
      <CustomTypography
        variant="body2"
        fontWeight={600}
        sx={{ color: colors.navyText, lineHeight: 1.2 }}
      >
        {qty}/{ordered}
      </CustomTypography>
      {vendor ? (
        <CustomTypography
          variant="caption"
          fontWeight={500}
          sx={{
            color: colors.greyTextColor,
            lineHeight: 1.25,
            maxWidth: 140,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={vendor}
        >
          {vendor}
        </CustomTypography>
      ) : null}
    </Box>
  );
};

const VariantsCell: React.FC<{ row: DailyTasksListRow }> = ({ row }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const variantsQty = row.variants_qty ?? 0;
  const ordered = row.ordered_quantity ?? 0;
  const coverage = variantsCoverage(variantsQty, ordered);
  const tone: DailyTasksChipTone =
    coverage === "full" ? "ok" : coverage === "partial" ? "info" : "warn";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35, py: 0.25 }}>
      <CustomTypography
        variant="body2"
        fontWeight={600}
        sx={{ color: colors.navyText, lineHeight: 1.2 }}
      >
        {variantsQty}/{ordered}
      </CustomTypography>
      <Chip
        size="small"
        label={VARIANTS_COVERAGE_LABELS[coverage]}
        sx={dailyTasksChipSx(theme, tone)}
      />
    </Box>
  );
};

const DailyTasksTable: React.FC<DailyTasksTableProps> = ({
  rows,
  stage,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  filters,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { canViewDispatchDate } = useDailyTasksFieldPermissions();
  const stageUi = dailyTasksStageUi(stage);
  const isMockup = stage === "mockup_upload" || stage === "mockup_approval";
  const isFile = stageUi.ledger === "file";
  const isPr = stageUi.ledger === "pr";
  const isPo = stageUi.ledger === "po";
  const isRaw = stageUi.ledger === "raw";
  const isPrint = stageUi.ledger === "print";
  const isPrintIn = stageUi.ledger === "print_in";
  const isProject = stageUi.ledger === "project";
  const isSlaQueue = stageUi.isSlaQueue;
  const cellCount =
    (isMockup
      ? 17
      : isPr || isProject
        ? 18
        : isRaw || isPrintIn
          ? 18
          : isFile || isPo || isPrint
            ? 16
            : 15) - (canViewDispatchDate ? 0 : 1);
  const daysValue = (row: DailyTasksListRow) =>
    isSlaQueue ? row.days_to_sla : row.days_to_dispatch;

  const headerSort = (column: DailyTasksSortBy) => ({
    sortColumn: column,
    sortBy,
    sortDirection: sortType,
    onSortChange: (nextColumn: string, next: SortType) =>
      onSortChange(nextColumn as DailyTasksSortBy, next),
    fontWeight: 500,
    fontSize: CELL_SIZE,
  });

  const cellSx = {
    fontWeight: CELL_WEIGHT,
    fontSize: CELL_SIZE,
    color: colors.navyText,
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2.5,
        borderRadius: 2,
        borderColor: colors.softBorder,
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <AppTableHeaderCell
                title="Brand"
                minWidth="140px"
                txtAlign="left"
                {...headerSort("brand_name")}
              />
              <AppTableHeaderCell
                title="Order"
                minWidth="90px"
                txtAlign="left"
                {...headerSort("order_id")}
              />
              <AppTableHeaderCell
                title="Product"
                minWidth="200px"
                txtAlign="left"
                {...headerSort("product_name")}
              />
              <AppTableHeaderCell
                title="Qty"
                minWidth="70px"
                fontWeight={500}
                fontSize={CELL_SIZE}
              />
              <AppTableHeaderCell
                title="Start"
                minWidth="110px"
                {...headerSort("start_date")}
              />
              {canViewDispatchDate ? (
                <AppTableHeaderCell
                  title="Dispatch"
                  minWidth="110px"
                  {...headerSort("dispatch_date")}
                />
              ) : null}
              {isRaw || isPrintIn ? (
                <>
                  <AppTableHeaderCell
                    title="Exp. inward"
                    minWidth="130px"
                    fontWeight={500}
                    fontSize={CELL_SIZE}
                  />
                  <AppTableHeaderCell
                    title="Print in TL"
                    minWidth="130px"
                    fontWeight={500}
                    fontSize={CELL_SIZE}
                  />
                </>
              ) : null}
              {isSlaQueue ? (
                <>
                  <AppTableHeaderCell
                    title="Status"
                    minWidth={isPr || isProject ? "150px" : "130px"}
                    txtAlign="left"
                    {...headerSort("mockup_work_state")}
                  />
                  <AppTableHeaderCell
                    title="SLA"
                    minWidth="120px"
                    {...headerSort("sla_status")}
                  />
                  <AppTableHeaderCell
                    title="Days left"
                    minWidth="110px"
                    {...headerSort("days_to_dispatch")}
                  />
                  {isMockup ? (
                    <>
                      <AppTableHeaderCell
                        title="Logos"
                        minWidth="80px"
                        fontWeight={500}
                        fontSize={CELL_SIZE}
                      />
                      <AppTableHeaderCell
                        title="Mockups"
                        minWidth="130px"
                        fontWeight={500}
                        fontSize={CELL_SIZE}
                        txtAlign="left"
                      />
                    </>
                  ) : (
                    <>
                      {isPr || isProject ? (
                        <AppTableHeaderCell
                          title="Variants"
                          minWidth="110px"
                          fontWeight={500}
                          fontSize={CELL_SIZE}
                          txtAlign="left"
                        />
                      ) : null}
                      <AppTableHeaderCell
                        title={
                          isPr
                            ? "PR qty"
                            : isProject
                              ? "Coverage"
                              : isPo
                                ? "POs"
                                : isRaw || isPrint || isPrintIn
                                  ? "Qty"
                                  : "Files"
                        }
                        minWidth={isPr || isProject ? "120px" : "130px"}
                        fontWeight={500}
                        fontSize={CELL_SIZE}
                        txtAlign="left"
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <AppTableHeaderCell
                    title="Days"
                    minWidth="90px"
                    {...headerSort("days_to_dispatch")}
                  />
                  <AppTableHeaderCell
                    title="Logos"
                    minWidth="80px"
                    fontWeight={500}
                    fontSize={CELL_SIZE}
                  />
                </>
              )}
              <AppTableHeaderCell
                title="PM"
                minWidth="120px"
                txtAlign="left"
                {...headerSort("pm_poc")}
              />
              <AppTableHeaderCell
                title="Sales"
                minWidth="120px"
                txtAlign="left"
                {...headerSort("sales_poc")}
              />
              <AppTableHeaderCell
                title="Comments"
                minWidth="90px"
                fontWeight={500}
                fontSize={CELL_SIZE}
              />
              {isSlaQueue ? (
                <>
                  {isFile ? (
                    <>
                      <AppTableHeaderCell
                        title="File TAT"
                        minWidth="110px"
                        fontWeight={500}
                        fontSize={CELL_SIZE}
                      />
                      <AppTableHeaderCell
                        title="Send TAT"
                        minWidth="120px"
                        fontWeight={500}
                        fontSize={CELL_SIZE}
                      />
                    </>
                  ) : isPr || isProject ? (
                    <>
                      <AppTableHeaderCell
                        title="Proj finance"
                        minWidth="110px"
                        fontWeight={500}
                        fontSize={CELL_SIZE}
                      />
                      {isPr ? (
                        <>
                          <AppTableHeaderCell
                            title="First PR"
                            minWidth="110px"
                            fontWeight={500}
                            fontSize={CELL_SIZE}
                          />
                          <AppTableHeaderCell
                            title="Full qty"
                            minWidth="120px"
                            fontWeight={500}
                            fontSize={CELL_SIZE}
                          />
                        </>
                      ) : (
                        <>
                          <AppTableHeaderCell
                            title="Queue SLA"
                            minWidth="110px"
                            fontWeight={500}
                            fontSize={CELL_SIZE}
                          />
                          <AppTableHeaderCell
                            title="Elapsed"
                            minWidth="120px"
                            fontWeight={500}
                            fontSize={CELL_SIZE}
                          />
                        </>
                      )}
                    </>
                  ) : isPo ? (
                    <>
                      <AppTableHeaderCell
                        title="First PO"
                        minWidth="110px"
                        fontWeight={500}
                        fontSize={CELL_SIZE}
                      />
                      <AppTableHeaderCell
                        title="All POs"
                        minWidth="120px"
                        fontWeight={500}
                        fontSize={CELL_SIZE}
                      />
                    </>
                  ) : isRaw || isPrint || isPrintIn ? (
                    <>
                      <AppTableHeaderCell
                        title="Queue SLA"
                        minWidth="110px"
                        fontWeight={500}
                        fontSize={CELL_SIZE}
                      />
                      <AppTableHeaderCell
                        title="Elapsed"
                        minWidth="120px"
                        fontWeight={500}
                        fontSize={CELL_SIZE}
                      />
                    </>
                  ) : (
                    <>
                      <AppTableHeaderCell
                        title="Upload TAT"
                        minWidth="110px"
                        {...headerSort("mockup_upload_tat_hours")}
                      />
                      <AppTableHeaderCell
                        title="Approval TAT"
                        minWidth="120px"
                        {...headerSort("mockup_approval_tat_hours")}
                      />
                    </>
                  )}
                </>
              ) : (
                <AppTableHeaderCell
                  title="Stage TAT"
                  minWidth="100px"
                  {...headerSort("stage_tat_hours")}
                />
              )}
              <AppTableHeaderCell
                title="Total TAT"
                minWidth="100px"
                {...headerSort("total_tat_hours")}
              />
              {isSlaQueue ? null : (
                <>
                  <AppTableHeaderCell
                    title="Preview"
                    minWidth="140px"
                    fontWeight={500}
                    fontSize={CELL_SIZE}
                    txtAlign="left"
                  />
                  <AppTableHeaderCell
                    title="Status"
                    minWidth="100px"
                    fontWeight={500}
                    fontSize={CELL_SIZE}
                  />
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={cellCount} sx={{ border: 0 }}>
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <CustomTypography
                      variant="heading6"
                      fontWeight={500}
                      sx={{ color: colors.greyTextColor }}
                    >
                      No products in this stage for the selected filters.
                    </CustomTypography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const days = daysValue(row);
                const daysOverdue =
                  days != null && days < 0 && row.stage_status !== "completed";
                const slaOverdue =
                  isSlaQueue &&
                  row.sla_status === "bad" &&
                  row.stage_status !== "completed";
                const commanderHref = row.commander_path || "";
                const productHref = `/products/${row.product_id}`;
                const logosYes = Boolean(row.logos_uploaded);

                const orderCell = (
                  <AppTableRowCell textAlign="left" {...cellSx}>
                    {commanderHref ? (
                      <Link
                        href={commanderHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        onClick={(event) => {
                          event.stopPropagation();
                          trackDailyTasksAction("link_order_open", filters, {
                            orderId: row.order_id,
                            context: { order_id: row.order_id },
                          });
                        }}
                        sx={{
                          color: colors.purple,
                          fontWeight: 500,
                          fontSize: CELL_SIZE,
                        }}
                      >
                        #{row.order_id}
                      </Link>
                    ) : (
                      <CustomTypography variant="body2" fontWeight={500}>
                        #{row.order_id}
                      </CustomTypography>
                    )}
                  </AppTableRowCell>
                );

                const brandCell = (
                  <AppTableRowCell
                    title={row.brand_name || "-"}
                    textAlign="left"
                    {...cellSx}
                  />
                );

                const productCell = (
                  <AppTableRowCell textAlign="left" {...cellSx}>
                    <Box>
                      <CustomTypography
                        variant="body2"
                        fontWeight={500}
                        sx={{ color: colors.navyText }}
                      >
                        {row.product_name || "-"}
                      </CustomTypography>
                      <Link
                        href={productHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        onClick={(event) => {
                          event.stopPropagation();
                          trackDailyTasksAction("link_product_open", filters, {
                            productId: row.product_id,
                            orderId: row.order_id,
                            context: {
                              product_id: row.product_id,
                              order_id: row.order_id,
                            },
                          });
                        }}
                        sx={{
                          color: colors.purple,
                          fontWeight: 500,
                          fontSize: "0.75rem",
                        }}
                      >
                        #{row.product_id}
                      </Link>
                    </Box>
                  </AppTableRowCell>
                );

                const logosCell = (
                  <AppTableRowCell {...cellSx}>
                    <Chip
                      size="small"
                      label={logosYes ? "Yes" : "No"}
                      sx={dailyTasksChipSx(theme, logosYes ? "ok" : "warn")}
                    />
                  </AppTableRowCell>
                );

                const variantsCell = (
                  <AppTableRowCell textAlign="left" {...cellSx}>
                    <VariantsCell row={row} />
                  </AppTableRowCell>
                );

                const daysCell = (
                  <AppTableRowCell {...cellSx}>
                    {days == null ? (
                      <CustomTypography variant="body2" fontWeight={500}>
                        -
                      </CustomTypography>
                    ) : daysOverdue ? (
                      <Chip
                        size="small"
                        label={`${days}d`}
                        sx={dailyTasksChipSx(theme, "bad")}
                      />
                    ) : (
                      <CustomTypography
                        variant="body2"
                        fontWeight={500}
                        sx={{ color: colors.navyText }}
                      >
                        {days}d
                      </CustomTypography>
                    )}
                  </AppTableRowCell>
                );

                return (
                  <TableRow
                    key={`${row.order_id}-${row.product_id}`}
                    hover
                    onClick={() => onRowClick(row)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: slaOverdue
                        ? `${colors.lightred}24`
                        : !isSlaQueue && daysOverdue
                          ? `${colors.errorRed}12`
                          : undefined,
                      "&:hover": {
                        bgcolor: slaOverdue
                          ? `${colors.lightred}33`
                          : !isSlaQueue && daysOverdue
                            ? `${colors.errorRed}1F`
                            : undefined,
                      },
                    }}
                  >
                    {brandCell}
                    {orderCell}
                    {productCell}
                    <AppTableRowCell
                      title={String(row.ordered_quantity ?? 0)}
                      {...cellSx}
                    />
                    <AppTableRowCell
                      title={formatDate(row.start_date)}
                      {...cellSx}
                    />
                    {canViewDispatchDate ? (
                      <AppTableRowCell
                        title={formatDate(row.dispatch_date)}
                        {...cellSx}
                      />
                    ) : null}
                    {isRaw || isPrintIn ? (
                      <>
                        <AppTableRowCell
                          title={formatDateList(row.expected_inward_dates)}
                          {...cellSx}
                        />
                        <AppTableRowCell
                          title={formatDateList(row.print_inward_timelines)}
                          {...cellSx}
                        />
                      </>
                    ) : null}
                    {isSlaQueue ? (
                      <>
                        <AppTableRowCell textAlign="left" {...cellSx}>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {(isPr
                              ? prTableStatusChips(row)
                              : isRaw || isPrintIn
                                ? rawTableStatusChips(row)
                                : row.mockup_work_state
                                  ? [
                                      {
                                        label: workStateLabel(
                                          row.mockup_work_state,
                                        ),
                                        tone: workStateChipTone(
                                          row.mockup_work_state,
                                        ),
                                      },
                                    ]
                                  : []
                            ).map((chip) => (
                              <Chip
                                key={chip.label}
                                size="small"
                                label={chip.label}
                                sx={dailyTasksChipSx(theme, chip.tone)}
                              />
                            ))}
                          </Box>
                        </AppTableRowCell>
                        <AppTableRowCell {...cellSx}>
                          {row.sla_status ? (
                            <Chip
                              size="small"
                              label={formatSlaChip(
                                row.sla_elapsed_hours,
                                row.sla_target_hours,
                              )}
                              sx={dailyTasksChipSx(
                                theme,
                                slaChipTone(row.sla_status),
                              )}
                            />
                          ) : (
                            <CustomTypography variant="body2" fontWeight={500}>
                              -
                            </CustomTypography>
                          )}
                        </AppTableRowCell>
                        {daysCell}
                        {isMockup ? logosCell : null}
                        {isPr || isProject ? variantsCell : null}
                        {isPr ? (
                          <AppTableRowCell textAlign="left" {...cellSx}>
                            <PrQtyCell row={row} />
                          </AppTableRowCell>
                        ) : (
                          <AppTableRowCell
                            title={previewForStage(row, stage)}
                            textAlign="left"
                            {...cellSx}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        {daysCell}
                        {logosCell}
                      </>
                    )}
                    <AppTableRowCell
                      title={row.pm_poc || "Unassigned"}
                      textAlign="left"
                      {...cellSx}
                    />
                    <AppTableRowCell
                      title={row.sales_poc || "-"}
                      textAlign="left"
                      {...cellSx}
                    />
                    <AppTableRowCell
                      title={String(row.comments_count ?? 0)}
                      {...cellSx}
                    />
                    {isSlaQueue ? (
                      <>
                        {isPr || isProject ? (
                          <AppTableRowCell textAlign="left" {...cellSx}>
                            {(() => {
                              const chip = projectFinanceChip(
                                row.project_finance_status,
                              );
                              return chip ? (
                                <Chip
                                  size="small"
                                  label={chip.label}
                                  sx={dailyTasksChipSx(theme, chip.tone)}
                                />
                              ) : (
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={500}
                                >
                                  -
                                </CustomTypography>
                              );
                            })()}
                          </AppTableRowCell>
                        ) : null}
                        <AppTableRowCell
                          title={formatHours(
                            isFile
                              ? row.file_upload_tat_hours
                              : isPr
                                ? row.pr_creation_tat_hours
                                : isPo
                                  ? row.po_creation_tat_hours
                                  : isRaw || isPrint || isPrintIn || isProject
                                    ? row.sla_target_hours
                                    : row.mockup_upload_tat_hours,
                          )}
                          {...cellSx}
                        />
                        <AppTableRowCell
                          title={formatHours(
                            isFile
                              ? row.cdr_to_printer_tat_hours
                              : isPr ||
                                  isPo ||
                                  isRaw ||
                                  isPrint ||
                                  isPrintIn ||
                                  isProject
                                ? row.sla_elapsed_hours
                                : row.mockup_approval_tat_hours,
                          )}
                          {...cellSx}
                        />
                      </>
                    ) : (
                      <AppTableRowCell
                        title={formatHours(row.stage_tat_hours)}
                        {...cellSx}
                      />
                    )}
                    <AppTableRowCell
                      title={formatHours(row.total_tat_hours)}
                      {...cellSx}
                    />
                    {isSlaQueue ? null : (
                      <>
                        <AppTableRowCell
                          title={previewForStage(row, stage)}
                          textAlign="left"
                          {...cellSx}
                        />
                        <AppTableRowCell {...cellSx}>
                          <Chip
                            size="small"
                            label={
                              row.stage_status === "completed"
                                ? "Done"
                                : "Pending"
                            }
                            sx={dailyTasksChipSx(
                              theme,
                              row.stage_status === "completed" ? "ok" : "warn",
                            )}
                          />
                        </AppTableRowCell>
                      </>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <AppTablePagenation
        totalCount={total_count}
        limit={limit}
        pageNo={page_no}
        onPageChange={onPageChange}
        handleRowsChange={onRowsPerPageChange}
        fontColor={colors.greyTextColor}
      />
    </Paper>
  );
};

export default observer(DailyTasksTable);
